import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Briefcase, FileText, Search, User } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { STATUS_LABELS } from "@/lib/constants";

export function CommandSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (!q.trim()) return setRes(null);
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)), 200);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const groups = res ? [
    ["People", User, res.people.map((p) => ({ key: p.id, title: p.name, sub: `${p.email || ""} · ${p.person_type.replace("_", " ")}`, path: `/contractors/${p.id}` }))],
    ["Contracts", FileText, res.contracts.map((c) => ({ key: c.id, title: c.employment_id, sub: STATUS_LABELS[c.status], path: `/contracts/${c.id}` }))],
    ["Companies", Building2, res.companies.map((c) => ({ key: c.id, title: c.name, sub: c.abn ? `ABN ${c.abn}` : "", path: `/recruitment-companies?edit=${c.id}` }))],
    ["PTE companies", Briefcase, res.pte_companies.map((c) => ({ key: c.id, title: c.name, sub: c.abn ? `ABN ${c.abn}` : "", path: `/pte-companies?edit=${c.id}` }))],
  ].filter((g) => g[2].length) : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 gap-0 max-w-xl overflow-hidden" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 border-b border-slate-200 px-4">
          <Search className="h-4 w-4 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
            placeholder="Search contractors, emails, employment IDs, companies…" className="flex-1 py-4 text-sm outline-none bg-transparent" />
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {!q && <p className="text-xs text-slate-400 p-3">Type to search. Tip: press Ctrl/⌘ + K anywhere.</p>}
          {q && res && !groups.length && <p className="text-sm text-slate-500 p-3" data-testid="global-search-empty">No results for “{q}”</p>}
          {groups.map(([label, Icon, items]) => (
            <div key={label} className="mb-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 px-3 py-1">{label}</div>
              {items.map((it) => (
                <button key={it.key} type="button" onClick={() => go(it.path)} data-testid={`global-search-result-${it.key}`}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-blue-50">
                  <Icon className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-800">{it.title}</span>
                  <span className="text-xs text-slate-400 truncate">{it.sub}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
